import React from "react";
import { Box, Heading, HStack, Text, VStack } from "native-base";
import { buildDetailGroups, googleReviews } from "../../utils/venueDetails";

/**
 * "Venue details": the same content as the app's VenueDetailsSection —
 * AI summary, Google editorial summary, service options, meals, drinks,
 * amenities, parking, payment and Google reviews. (The AI accessibility
 * narrative is shown in the Accessibility details section.)
 */
export function VenueDetails({ venue }) {
  const groups = buildDetailGroups(venue);
  const reviews = googleReviews(venue);
  const aiPending = !!venue?.aiSummaryPending && !venue?.aiSummary;
  if (!venue?.aiSummary && !aiPending && !venue?.bizEditorialSummary && groups.length === 0 && reviews.length === 0) {
    return null;
  }

  return (
    <VStack space={3} mb={4} role="region" aria-label="Venue details">
      <Heading size="sm" color="coolGray.800" accessibilityRole="header" aria-level="2">
        Venue details
      </Heading>

      {venue.aiSummary ? (
        <VStack space={1}>
          <Text fontWeight="semibold" color="coolGray.800" fontSize="sm" accessibilityRole="header" aria-level="3">
            About this venue
          </Text>
          <Text color="coolGray.700" fontSize="sm">{venue.aiSummary}</Text>
          <Text color="coolGray.600" fontSize="xs">AI-generated summary</Text>
        </VStack>
      ) : aiPending ? (
        <Text color="coolGray.600" fontSize="sm">A venue summary is being prepared.</Text>
      ) : null}

      {venue.bizEditorialSummary ? (
        <Text color="coolGray.700" fontSize="sm">{venue.bizEditorialSummary}</Text>
      ) : null}

      {groups.map((g) => {
        return (
          <VStack key={g.title} space={1}>
            <Text fontWeight="semibold" color="coolGray.800" fontSize="sm" accessibilityRole="header" aria-level="3">
              {g.title}
            </Text>
            <ul style={{ display: "flex", flexWrap: "wrap", gap: 6, listStyle: "none", margin: 0, padding: 0 }}>
              {g.chips.map((c) => (
                <li
                  key={c.label}
                  style={{
                    fontSize: 13,
                    padding: "2px 8px",
                    borderRadius: 12,
                    border: `1px solid ${c.value ? "#86efac" : "#e5e7eb"}`,
                    background: c.value ? "#f0fdf4" : "#f9fafb",
                    color: c.value ? "#166534" : "#6b7280",
                    textDecoration: c.value ? "none" : "line-through",
                  }}
                >
                  <span aria-hidden="true">{c.value ? "✓ " : "✗ "}</span>
                  {c.label}
                  <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}>
                    {c.value ? " (available)" : " (not available)"}
                  </span>
                </li>
              ))}
            </ul>
          </VStack>
        );
      })}

      {reviews.length > 0 && (
        <VStack space={2}>
          <Text fontWeight="semibold" color="coolGray.800" fontSize="sm" accessibilityRole="header" aria-level="3">
            Google reviews
          </Text>
          {reviews.map((r, i) => {
            const stars = r.rating != null ? Math.max(0, Math.min(5, Math.floor(r.rating))) : null;
            return (
              <Box key={i} borderWidth={1} borderColor="coolGray.100" borderRadius="md" p={3}>
                <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap">
                  <Text fontWeight="semibold" fontSize="sm" color="coolGray.800">{r.author || "Anonymous"}</Text>
                  {stars != null && (
                    <Text fontSize="sm" color="amber.500" accessibilityLabel={`Rating: ${r.rating} out of 5`}>
                      {"★".repeat(stars) + "☆".repeat(5 - stars)}
                    </Text>
                  )}
                </HStack>
                {r.text ? <Text fontSize="sm" color="coolGray.700">{r.text}</Text> : null}
                {r.relativeTime ? <Text fontSize="xs" color="coolGray.600">{r.relativeTime}</Text> : null}
              </Box>
            );
          })}
        </VStack>
      )}
    </VStack>
  );
}

export default VenueDetails;
